<!-- eslint-disable perfectionist/sort-imports -->
<script setup lang="ts">
import {
  cellphoneInputMask,
  cellphoneToApiPayload,
  formatCellphoneMasked,
  formatCellphoneTyping,
  normalizeCellphoneDigits,
} from '@solfacil/frontend-utils/cellphone';
import { formatCpfMasked, normalizeCpfDigits } from '@solfacil/frontend-utils/cpf';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import {
  computed, nextTick, ref, watch,
} from 'vue';
import { toast } from 'vue3-toastify';

import ModalForm from '~/components/shared/ModalForm.vue';
import useFormApiErrors from '~/composables/useFormApiErrors';
import {
  type AssociateFormField,
  parseAssociateGeneralError,
  resolveAssociateFormApiErrors,
  shouldShowGeneralApiErrorInToast,
} from '~/utils/apiFieldErrors';
import type { RegisterAPI } from '~/services/register-api/register-api.types';
import { useUpdateAssociate } from '~/store/associates/useAssociateMutations';
import useAvailableUsers from '~/store/associates/useAvailableUsers';
import usePartnerUsers, { type PartnerUser } from '~/store/coreUsers/usePartnerUsers';

import {
  type AssociateFormData,
  associateFormSchema,
} from '../schemas/associateSchema';

export interface AssociateModalProps {
  associates: RegisterAPI.Associate[];
  companyId?: number;
  mode: 'edit' | 'view';
  modelValue: boolean;
  partnerId: number;
  selectedAssociate: null | RegisterAPI.Associate;
  usersPageSize?: number;
}

const props = defineProps<AssociateModalProps>();

const emit = defineEmits<{
  success: [];
  'update:modelValue': [value: boolean];
}>();

const modalFormRef = ref();

const updateMutation = useUpdateAssociate();

const usersParams = computed(() => ({
  page: 1,
  pageSize: props.usersPageSize ?? 10,
  partnerId: props.partnerId,
}));

const { data: usersList, isFetching: isFetchingUsers } = usePartnerUsers(
  computed(() => usersParams.value),
);

const allUsers = computed(() => usersList.value?.data ?? []);

const currentAssociateCpf = computed(() => props.selectedAssociate?.cpf ?? null);

const { availableUsers } = useAvailableUsers(
  allUsers,
  computed(() => props.associates),
  currentAssociateCpf,
);

const {
  defineField,
  errors,
  handleSubmit,
  resetForm,
  setErrors,
  setValues,
} = useForm<AssociateFormData>({
  initialValues: {
    celular: '',
    nomeCompleto: '',
    userId: undefined as number | undefined,
  },
  validateOnMount: false,
  validationSchema: toTypedSchema(associateFormSchema),
});

const fieldOptions = {
  validateOnBlur: true,
  validateOnChange: true,
  validateOnInput: true,
  validateOnModelUpdate: true,
};

const [nomeCompleto, nomeCompletoProps] = defineField('nomeCompleto', fieldOptions);
const [celular, celularProps] = defineField('celular', fieldOptions);
const [userId, userIdProps] = defineField('userId', fieldOptions);

const { applyFormApiErrors, clearFormApiError, formApiError } = useFormApiErrors<AssociateFormField>(setErrors);

function clearFieldApiError(field: AssociateFormField) {
  clearFormApiError();
  setErrors({ [field]: undefined });
}

const celularMasked = computed({
  get: () => celular.value,
  set: (value: string) => {
    celular.value = formatCellphoneTyping(value);
  },
});

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const modalTitle = computed(() => (
  props.mode === 'edit' ? 'Editar sócio' : 'Detalhes do sócio'
));

const isSubmitting = computed(() => updateMutation.isPending.value);

const persistedPhonePayload = ref('');

function applyAssociateToForm(associate: RegisterAPI.Associate) {
  const rawPhone = associate.phone ?? '';
  persistedPhonePayload.value = rawPhone ? cellphoneToApiPayload(rawPhone) : '';
  const formattedCelular = rawPhone
    ? formatCellphoneMasked(normalizeCellphoneDigits(rawPhone))
    : '';

  setValues({
    celular: formattedCelular,
    nomeCompleto: associate.name ?? '',
    userId: associate.user_id ?? undefined,
  });
}

function formatLinkedUserLabel(user: PartnerUser): string {
  const name = user.fullName?.trim() || 'Sem nome';
  const email = user.email?.trim();
  return email ? `${name} - (${email})` : name;
}

function formattedCpf(cpf: string): string {
  return formatCpfMasked(normalizeCpfDigits(cpf)) || cpf;
}

function resolvePhonePayload(phoneInput: string): string {
  const trimmed = phoneInput.trim();
  if (trimmed) {
    return cellphoneToApiPayload(trimmed);
  }
  return persistedPhonePayload.value;
}

watch(() => props.selectedAssociate, (associate) => {
  if (associate) {
    nextTick(() => applyAssociateToForm(associate));
  }
}, { immediate: true });

watch([nomeCompleto, celular, userId], () => {
  clearFormApiError();
});

watch(() => props.modelValue, (isOpen) => {
  if (!isOpen) {
    persistedPhonePayload.value = '';
    nextTick(() => {
      clearFormApiError();
      resetForm();
    });
    return;
  }

  clearFormApiError();

  if (props.selectedAssociate) {
    nextTick(() => applyAssociateToForm(props.selectedAssociate!));
  }
});

const handleSave = handleSubmit(async (values) => {
  if (!props.selectedAssociate) return;

  if (!props.companyId) {
    toast.error('Empresa não encontrada para atualizar o sócio.');
    return;
  }

  try {
    const updated = await updateMutation.mutateAsync({
      companyId: props.companyId,
      data: {
        document: normalizeCpfDigits(props.selectedAssociate.cpf),
        full_name: values.nomeCompleto,
        phone: resolvePhonePayload(values.celular),
        user_id: values.userId ?? null,
      },
    });

    if (updated.phone) {
      persistedPhonePayload.value = cellphoneToApiPayload(updated.phone);
    }

    emit('success');
    modalFormRef.value?.resetEditingState();
    isVisible.value = false;
  } catch (error) {
    if (shouldShowGeneralApiErrorInToast(error)) {
      clearFormApiError();
      toast.error(parseAssociateGeneralError(error, 'Erro ao atualizar sócio'));
      return;
    }

    applyFormApiErrors(error, (err) => resolveAssociateFormApiErrors(err, 'Erro ao atualizar sócio'));
  }
});

function handleCancel() {
  isVisible.value = false;
}

function handleEdit() {
  emit('update:modelValue', false);
  nextTick(() => emit('update:modelValue', true));
}
</script>

<template>
  <ModalForm
    ref="modalFormRef"
    v-model="isVisible"
    :title="modalTitle"
    :mode="mode"
    :loading="isSubmitting"
    size="lg"
    @save="handleSave"
    @edit="handleEdit"
    @cancel="handleCancel"
  >
    <template
      #default="{
        isEditMode: modalIsEditMode,
        isLoading: isSubmittingForm,
      }"
    >
      <div class="row">
        <div v-if="formApiError" class="col-12">
          <div class="alert alert-danger mb-3 py-2" role="alert">
            {{ formApiError }}
          </div>
        </div>

        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label" for="associateCpf">CPF</label>
            <input
              id="associateCpf"
              type="text"
              class="form-control"
              :value="selectedAssociate?.cpf ? formattedCpf(selectedAssociate.cpf) : ''"
              disabled
            />
          </div>
        </div>

        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label" for="associateEmail">E-mail</label>
            <input
              id="associateEmail"
              type="email"
              class="form-control"
              :value="selectedAssociate?.email ?? ''"
              disabled
            />
          </div>
        </div>

        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label required" for="associateName">Nome completo</label>
            <input
              id="associateName"
              v-model="nomeCompleto"
              v-bind="nomeCompletoProps"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.nomeCompleto }"
              :disabled="!modalIsEditMode || isSubmittingForm"
              @input="clearFieldApiError('nomeCompleto')"
            />
            <div v-if="errors.nomeCompleto" class="invalid-feedback">
              {{ errors.nomeCompleto }}
            </div>
          </div>
        </div>

        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label" for="associateCelular">Celular</label>
            <input
              id="associateCelular"
              v-model="celularMasked"
              v-bind="celularProps"
              type="tel"
              inputmode="numeric"
              class="form-control"
              :class="{ 'is-invalid': errors.celular }"
              :placeholder="cellphoneInputMask"
              maxlength="15"
              :disabled="!modalIsEditMode || isSubmittingForm"
              @input="clearFieldApiError('celular')"
            />
            <div v-if="errors.celular" class="invalid-feedback">
              {{ errors.celular }}
            </div>
          </div>
        </div>

        <div class="col-12">
          <div class="mb-0">
            <label
              class="form-label"
              :class="{ required: modalIsEditMode }"
              for="associateUser"
            >Usuário vinculado</label>
            <select
              id="associateUser"
              v-model="userId"
              v-bind="userIdProps"
              class="form-select"
              :class="{ 'is-invalid': errors.userId }"
              :disabled="!modalIsEditMode || isFetchingUsers || isSubmittingForm"
              @change="clearFieldApiError('userId')"
            >
              <option v-if="isFetchingUsers" :value="undefined" disabled>
                Carregando usuários...
              </option>
              <template v-else>
                <option :value="undefined">
                  Sem vínculo
                </option>
                <option
                  v-for="user in availableUsers"
                  :key="user.id"
                  :value="user.id"
                >
                  {{ formatLinkedUserLabel(user) }}
                </option>
              </template>
            </select>
            <div v-if="errors.userId" class="invalid-feedback">
              {{ errors.userId }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </ModalForm>
</template>
